import { useMemo } from "react"

import { eachDayOfInterval, endOfWeek, format } from "date-fns"

import {
  useCollectionsConfig,
  useRoutesPlanned,
  useVehicleTypes,
  useWeeklyPlanSnapshot,
} from "@/hooks/useCollections"
import { WeekData, buildLiveWeekData, snapshotToWeekData } from "@/lib/weeklyPlan"

function useLiveWeekData(
  weekStart: Date,
  weekEnd: Date,
  weekDays: Date[],
): WeekData & { isLoading: boolean } {
  const { data: routesPlannedData, isLoading: routesLoading } = useRoutesPlanned()
  const { data: vehicleTypesData } = useVehicleTypes()
  const { data: configData } = useCollectionsConfig()

  const isLoading = routesLoading

  const result = useMemo(
    () =>
      buildLiveWeekData({
        routes: routesPlannedData?.data,
        vehicleTypes: vehicleTypesData?.data,
        config: configData?.data,
        weekStart,
        weekEnd,
        weekDays,
      }),
    [routesPlannedData, vehicleTypesData, configData, weekStart, weekEnd, weekDays],
  )

  return { ...result, isLocked: false, isLoading }
}

/** The week's locked snapshot if there is one, otherwise the live plan priced as it stands. */
export function useWeekData(weekStart: Date) {
  const weekEnd = useMemo(() => endOfWeek(weekStart, { weekStartsOn: 1 }), [weekStart])
  const weekDays = useMemo(
    () => eachDayOfInterval({ start: weekStart, end: weekEnd }),
    [weekStart, weekEnd],
  )
  const weekStartStr = format(weekStart, "yyyy-MM-dd")

  const { data: snapshot, isLoading: snapshotLoading } = useWeeklyPlanSnapshot(weekStartStr)
  const liveData = useLiveWeekData(weekStart, weekEnd, weekDays)

  const hasSnapshot = !!snapshot?.snapshot
  const data: WeekData = useMemo(() => {
    if (hasSnapshot) return snapshotToWeekData(snapshot!.snapshot, weekDays)
    return liveData
  }, [hasSnapshot, snapshot, liveData, weekDays])

  const isLoading = snapshotLoading || (!hasSnapshot && liveData.isLoading)

  return { weekEnd, weekDays, data, isLoading }
}
